import type { EventUI } from "@jesus-central-church/ui";
import type { ISbStoryData } from "@storyblok/js";
import type { SbEvent } from "../../.storyblok/types/287435740670216/storyblok-components";
import { storyblokConfig } from "./config";
import { getStoryblokApi } from "./storyblok";

/**
 * Event type labels mapping (Storyblok value -> Dutch display label)
 */
export const EVENT_TYPE_LABELS: Record<string, string> = {
  service: "Dienst",
  event: "Event",
  youth: "Jongeren",
  teens: "Tieners",
  disciples: "Discipelen",
  prayer: "Gebed",
};

/**
 * Maps a Storyblok event story to the EventUI type used by UI components
 */
export function mapStoryblokEventToUI(story: ISbStoryData<SbEvent>): EventUI {
  const content = story.content;
  const slug =
    story.full_slug?.replace(`${storyblokConfig.basePath}/`, "") ||
    story.slug ||
    "";

  return {
    id: story.uuid || story.id?.toString() || content._uid,
    slug,
    title: content.title,
    excerpt: content.description,
    startDateTime: content.date,
    location: content.location || "Rijsselseweg 1, 2803PZ Gouda",
    coverImage: content.thumbnail?.filename
      ? {
        src: content.thumbnail.filename,
        alt: content.thumbnail.alt || content.title,
      }
      : // eslint-disable-next-line unicorn/no-nested-ternary
      content.video?.filename
        ? { src: content.video.filename, alt: content.title }
        : undefined,
    type: content.type,
    typeLabel: content.type ? EVENT_TYPE_LABELS[content.type] : undefined,
    speaker: content.speaker,
    youtubeUrl: content.youtubeLink?.url || content.youtubeLink?.cached_url,
    featured: false, // Can be determined by other logic if needed
    weeztixShopUrl:
      content.weeztixShopUrl?.url || content.weeztixShopUrl?.cached_url,
  };
}

/**
 * Fetches all upcoming events from Storyblok
 * @param locale - Optional locale for translations (default: "nl")
 * @returns Array of EventUI objects sorted by date ascending
 */
export async function getAllUpcomingEvents(
  locale?: string,
  filterByType?: string,
): Promise<EventUI[]> {
  const storyblokApi = getStoryblokApi();
  const basePath = storyblokConfig.basePath || "jesuscentral";

  const { data: eventsResponse } = await storyblokApi.get("cdn/stories", {
    starts_with: `${basePath}/activiteiten/`,
    version: storyblokConfig.version,
    language: locale || "nl",
    filter_query: {
      ...(filterByType ? { type: { in: filterByType } } : {}),
      date: {
        gt_date: new Date().toISOString().split("T")[0],
      },
    },
    sort_by: "content.date:asc",
    per_page: 100,
    cv: Date.now(),
  });

  const { stories } = eventsResponse;

  return stories.map((story: ISbStoryData<SbEvent>) =>
    mapStoryblokEventToUI(story),
  );
}

export const getNextServiceEvent = async (
  locale?: string,
): Promise<EventUI | null> => {
  const storyblokApi = getStoryblokApi();
  const basePath = storyblokConfig.basePath || "jesuscentral";

  const { data: eventResponse } = await storyblokApi.get("cdn/stories", {
    starts_with: `${basePath}/activiteiten/`,
    version: storyblokConfig.version,
    language: locale || "nl",
    filter_query: {
      date: {
        gt_date: new Date().toISOString().split("T")[0],
      },
      type: { in: "service" },
    },
    sort_by: "content.date:asc",
    per_page: 1,
  });

  const { stories } = eventResponse;

  return (
    stories.map((story: ISbStoryData<SbEvent>) =>
      mapStoryblokEventToUI(story),
    )[0] || null
  );
};
